import { useLayoutEffect, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react'
import type { CellEditorProps } from './registry'
import { controlChrome, editorBehavior } from './editorChrome'

/** PR275 review blocker — the INVALID sentinel a kind's `parse` returns for
 *  input it cannot accept (e.g. a non-numeric draft for a number). The editor
 *  then REFUSES to commit: a visible refusal appears, the draft stays for
 *  correction, Escape keeps the prior value, and blur cancels instead of
 *  committing — invalid input can never become a clearing (or any) write that
 *  reads as success. Intentional blank-to-clear is untouched: '' parses to
 *  null in the kinds that support clearing. */
export const INVALID_INPUT: unique symbol = Symbol('invalid-input')

/**
 * Shared inline input editor for text-like kinds (text, number, email…). Owns
 * the input chrome + the commit/cancel keyboard contract ONCE, so every input
 * editor looks and behaves identically (and can't drift). Kinds differ only by
 * how they format the value into a draft (`toDraft`) and parse it back
 * (`parse`), plus any native input attributes (`inputProps`).
 *
 *  - cell context: Enter / blur → commit parse(draft); Esc → cancel
 *  - filter context (SEN-704): commit parse(draft) on EVERY change. Radix
 *    popovers unmount content on pointerdown-outside BEFORE blur fires, so a
 *    blur-committed value silently never reaches the filter tree. In filter
 *    context a commit does not unmount the editor (FilterValue.onCommit just
 *    updates the tree), so per-change commit is safe — and makes the committed
 *    state always equal the visible state.
 *  - form context: NO autofocus — in a multi-field form every mounted editor
 *    autofocusing means the LAST field steals focus (the SEU-category "why is
 *    Icon highlighted" bug). The form owns focus (CreateRecordModal focuses
 *    its first field); commit does not unmount (fields persist).
 *  - size=1 so the input imposes no intrinsic width (see text-cell widen fix)
 *  - `wrap` (SEN-197, long text): where the context `expands` (a table cell),
 *    the editor is a wrapping box pinned to the cell's top-left at the cell's
 *    width that grows DOWN to fit the whole value, laid over the rows —
 *    absolutely positioned, so the column never widens (228fbb3c) and the row
 *    never grows. Same keyboard contract: Enter commits (no newline), Esc
 *    cancels, blur commits.
 */
export function InlineEditor({
  value,
  onCommit,
  onCancel,
  context,
  toDraft = (v) => (v == null ? '' : String(v)),
  parse = (s) => s,
  inputProps,
  wrap = false,
}: CellEditorProps & {
  toDraft?: (v: unknown) => string
  parse?: (s: string) => unknown
  inputProps?: InputHTMLAttributes<HTMLInputElement>
  /** The kind's values can be long prose — expand to show them where the
   *  context allows (editorChrome `expands`). */
  wrap?: boolean
}) {
  const expands = wrap && editorBehavior(context).expands
  // The box keeps the single-line input's value rule (review #5): an <input>
  // strips line breaks from its value, so a stored or pasted newline never
  // reached a commit. No attribute declares multiline text today; until one
  // does, the expanding box strips them the same way.
  const singleLine = (s: string) => (expands ? s.replace(/[\r\n]/g, '') : s)
  const [draft, setDraft] = useState(() => singleLine(toDraft(value)))
  const [invalid, setInvalid] = useState(false)
  const commit = (s: string): boolean => {
    const parsed = parse(s)
    if (parsed === INVALID_INPUT) {
      setInvalid(true)
      return false
    }
    setInvalid(false)
    onCommit(parsed)
    return true
  }
  // No debounce by design (v1): transient prefix commits (typing '1' of '15')
  // hit React-Query-keyed caches and are cheap; add a debounce knob here if
  // the live stack ever shows pressure.
  const live = context === 'filter'
  const boxRef = useRef<HTMLTextAreaElement>(null)
  // Grow the expanding box to its content on every draft change.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft, expands])
  const onDraft = (next: string) => {
    setDraft(next)
    if (live) commit(next)
    else if (invalid && parse(next) !== INVALID_INPUT) setInvalid(false)
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit(draft)
    } else if (e.key === 'Escape') {
      // Escape belongs to the EDITOR while one is open: cancel the draft
      // only. Whether the key is consumed locally or bubbles to the
      // surface (record stage's Escape-closes, the modal's topmost-Escape
      // stack) is a declared context CAPABILITY — editorChrome's
      // escapeIsLocal row, never a which-context-am-I test inline
      // (PR277 review finding 4 + the editorChrome file rule).
      e.preventDefault()
      if (editorBehavior(context).escapeIsLocal) e.stopPropagation()
      onCancel()
    }
  }
  const onBlur = () => {
    // A blur while the draft is unacceptable CANCELS (prior value kept)
    // — it must never fall through to a commit that clears or rewrites.
    if (parse(draft) === INVALID_INPUT) onCancel()
    else commit(draft)
  }
  if (expands) {
    return (
      <span className="relative block w-full min-w-0">
        {/* Holds the cell's own single-line height while the box floats. */}
        <span aria-hidden className="invisible block px-2 py-1.5 text-sm">&nbsp;</span>
        <textarea
          ref={boxRef}
          autoFocus={editorBehavior(context).autoFocus}
          rows={1}
          data-testid="cell-editor"
          value={draft}
          onChange={(e) => onDraft(singleLine(e.target.value))}
          onKeyDown={(e) => {
            // The box owns vertical caret movement between its wrapped lines:
            // the table's row navigation must never see these (review #5).
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.stopPropagation()
              return
            }
            onKeyDown(e)
          }}
          onBlur={onBlur}
          aria-invalid={invalid || undefined}
          className={`absolute left-0 top-0 z-30 block max-h-[50vh] w-full min-w-0 resize-none overflow-auto whitespace-pre-wrap break-words rounded-sm bg-background px-2 py-1.5 text-sm leading-snug text-foreground shadow-float outline-none ${invalid ? 'ring-1 ring-destructive' : ''} ${controlChrome(context)}`}
        />
      </span>
    )
  }
  return (
    <span className="relative block w-full min-w-0">
    <input
      autoFocus={editorBehavior(context).autoFocus}
      size={1}
      data-testid="cell-editor"
      value={draft}
      onChange={(e) => onDraft(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      aria-invalid={invalid || undefined}
      className={`w-full min-w-0 rounded-sm bg-background px-2 py-1.5 text-sm text-foreground outline-none ${invalid ? 'ring-1 ring-destructive' : ''} ${controlChrome(context)}`}
      {...inputProps}
    />
    {invalid && (
      <span data-testid="cell-editor-invalid" role="alert"
        className="absolute left-0 top-full z-20 mt-0.5 max-w-56 whitespace-normal rounded-sm bg-destructive px-1.5 py-0.5 text-[11px] leading-snug text-destructive-foreground shadow">
        Not a valid value — fix it, or press Esc to keep the current value
      </span>
    )}
    </span>
  )
}
